/* NHI Dashboard */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  Search, GripVertical,
  Users, UserX, Key, Lock, RefreshCw,
  UserCheck, Gauge, Target, Shield,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Clock, Database, Eye, ShieldAlert,
  ArrowRight, Network, FileKey, Globe, Server, Link, Activity
} from "lucide-react";
import { CloudSelector } from "../components/CloudSelector";
import {
  fetchTenants,
  fetchAwsAccounts,
  fetchGcpProjectIds,
  fetchAzureSubscriptions,
  fetchTotalNhis,
  fetchActiveSecrets,
  fetchCriticalRisks,
  fetchOverallRiskScore,
  fetchNhiLifecycleRegistry,
  fetchRotation,
  fetchStaticCredentials,
  fetchSecretsPosture,
  fetchNhiReview,
  fetchNhiCorrelations,
  fetchCloudSaasCorrelations,
  fetchConfiguredSaasApps,
  refreshDashboardSnapshots,
} from "../services/api";
import type { CloudType, Tenant, CloudResource } from "../types/cloud";
import type {
  TotalNhisResponse,
  ActiveSecretsResponse,
  CriticalRisksResponse,
  OverallRiskScoreResponse,
  NhiLifecycleRegistryResponse,
  RotationResponse,
  StaticCredentialsResponse,
  SecretsPostureResponse,
  NhiReviewResponse,
  NhiCorrelationsResponse,
  CloudSaasCorrelation,
} from "../types/dashboard";
import { ExpandedCardDialog } from "../components/cards/ExpandedCardDialog";
import { myLocalStorage } from "../../../StorageHelper";
import { getRatioPercentage, getRelativePercentage } from "../lib/utils";
import {
  ExpandedRiskScoreCard,
  ExpandedLifecycleCard,
  ExpandedRotationCard,
  ExpandedCredentialsCard,
  ExpandedSecretsCard,
  ExpandedHumanCard,
  ExpandedCorrelationsCard,
  ExpandedTotalNhisCard,
  ExpandedTotalSecretsCard,
  ExpandedReviewAgedNhisCard,
  ExpandedSecretsCredentialsCard
} from "../components/cards/expanded";
import { Skeleton } from "../../../ui/common-ui/skeleton";

// ============= Dashboard Data State =============
interface DashboardData {
  totalNhis: TotalNhisResponse | null;
  activeSecrets: ActiveSecretsResponse | null;
  criticalRisks: CriticalRisksResponse | null;
  riskScore: OverallRiskScoreResponse | null;
  lifecycle: NhiLifecycleRegistryResponse | null;
  rotation: RotationResponse | null;
  credentials: StaticCredentialsResponse | null;
  secrets: SecretsPostureResponse | null;
  review: NhiReviewResponse | null;
  correlations: NhiCorrelationsResponse | null;
}

const emptyDashboard: DashboardData = {
  totalNhis: null,
  activeSecrets: null,
  criticalRisks: null,
  riskScore: null,
  lifecycle: null,
  rotation: null,
  credentials: null,
  secrets: null,
  review: null,
  correlations: null,
};

// Issue icon mapping for secrets posture
const issueIconMap: Record<string, any> = {
  "Aged greater than 90 days": Clock,
  "Outside vault": ShieldAlert,
  "High access scope": Network,
  "No rotation policy": RefreshCw,
};

// Card size configurations
type CardSize = "small" | "medium" | "large" | "wide" | "tall" | "largeTall" | "full";

interface CardConfig {
  id: string;
  size: CardSize;
  expandable: boolean;
}

const cardConfigs: CardConfig[] = [
  { id: "riskScore", size: "medium", expandable: true },
  { id: "correlations", size: "largeTall", expandable: true },
  { id: "total-nhis", size: "tall", expandable: true },
  { id: "review-aged-nhis", size: "tall", expandable: true },
  { id: "lifecycle", size: "large", expandable: false },
  { id: "total-secrets", size: "tall", expandable: true },
  { id: "secretsCredentials", size: "tall", expandable: true },
];

const getSizeClasses = (size: CardSize): string => {
  // We only return col-span classes now; vertical spanning is calculated via ResizeObserver
  switch (size) {
    case "small": return "col-span-1";
    case "medium": return "col-span-1";
    case "large": return "col-span-1 lg:col-span-2";
    case "wide": return "col-span-1 lg:col-span-2";
    case "tall": return "col-span-1";
    case "largeTall": return "col-span-1 lg:col-span-2";
    case "full": return "col-span-1 sm:col-span-2 lg:col-span-4";
    default: return "col-span-1";
  }
};

interface SortableCardPropsExtended {
  id: string;
  size: CardSize;
  content: React.ReactNode;
  activeId: string | null;
  expandedCard: string | null;
  registerNode: (id: string, node: HTMLDivElement | null) => void;
}

const SortableCard = ({ id, size, content, activeId, expandedCard, registerNode }: SortableCardPropsExtended) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // Normalize transform to prevent scale distortion during reorder
  const normalizedTransform = transform
    ? { ...transform, scaleX: 1, scaleY: 1 }
    : null;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(normalizedTransform),
    transition: isDragging ? undefined : transition,
    zIndex: isDragging ? 50 : 1,
  };

  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node);
    registerNode(id, node);
  }, [setNodeRef, registerNode, id]);

  // Dynamic Row Spanning for dense packing
  const innerRef = useRef<HTMLDivElement>(null);
  const [rowSpan, setRowSpan] = useState<number>(300); // Default estimate

  useEffect(() => {
    if (!innerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.target.getBoundingClientRect().height;
        setRowSpan(Math.ceil(height) + 16);
      }
    });
    observer.observe(innerRef.current);
    
    const mutObserver = new MutationObserver(() => {
      if (innerRef.current) {
        setRowSpan(Math.ceil(innerRef.current.getBoundingClientRect().height) + 16);
      }
    });
    mutObserver.observe(innerRef.current, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutObserver.disconnect();
    };
  }, []);

  // Only disable click interactions when a card modal is expanded — NOT during drag
  const isModalOpen = expandedCard !== null;

  return (
    <div
      ref={combinedRef}
      style={{
        ...style,
        gridRowEnd: `span ${rowSpan}`
      }}
      className={`relative group min-w-0 min-h-0 ${getSizeClasses(size)} ${
        isDragging ? "opacity-40" : ""
      } overflow-visible`}
    >
      <div 
        ref={innerRef}
        className={`w-full ${isModalOpen ? 'pointer-events-none' : ''}`}
      >
        <div
          {...attributes}
          {...listeners}
          className="absolute top-3 right-3 z-[60] opacity-0 group-hover:opacity-100 transition-all cursor-grab active:cursor-grabbing p-1.5 rounded-lg bg-background/80 backdrop-blur border border-border/50 hover:bg-muted"
        >
          <GripVertical className="w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
        {content}
      </div>
    </div>
  );
};

interface CardWrapperProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  clickable?: boolean;
}

const CardWrapper = ({ children, className = "", onClick, clickable = false }: CardWrapperProps) => (
  <div
    className={`group/card relative h-full min-w-0 flex flex-col p-5 rounded-2xl border border-border/30 bg-card/95 backdrop-blur-sm shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08),0_8px_32px_rgba(0,0,0,0.04)] hover:border-sky-500/25 transition-all duration-500 overflow-hidden ${clickable ? "cursor-pointer hover:-translate-y-0.5" : ""} ${className}`}
    onClick={onClick}
  >
    {/* Subtle gradient shimmer on hover */}
    <div className="absolute inset-0 bg-gradient-to-br from-sky-500/[0.02] via-transparent to-accent/[0.02] opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl" />
    <div className="relative flex flex-col flex-1 min-h-0">
      {children}
    </div>
  </div>
);

const CardHeader = ({ icon: Icon, title, badge }: { icon: any; title: string; badge?: React.ReactNode }) => (
  <div className="flex items-center gap-2.5 mb-4 flex-wrap">
    <div className="p-2 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-sky-500/10">
      <Icon className="w-4 h-4 text-sky-500" />
    </div>
    <h3 className="font-semibold text-[14px] text-foreground tracking-tight">{title}</h3>
    {badge && <div className="ml-auto">{badge}</div>}
  </div>
);

// Loading skeleton for cards
const CardSkeleton = ({ className = "" }: { className?: string }) => (
  <CardWrapper className={className}>
    <div className="space-y-3">
      <Skeleton className="h-4 w-32 rounded-lg" />
      <Skeleton className="h-8 w-20 rounded-lg" />
      <Skeleton className="h-2 w-full rounded-full" />
      <Skeleton className="h-2 w-3/4 rounded-full" />
    </div>
  </CardWrapper>
);

const setStructuredLocalStorageValue = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify({ value }));
};

// ==================== CARD COMPONENTS ====================

const RiskScoreCard = ({ data, selectedCloud, onClick }: { data: OverallRiskScoreResponse | null; selectedCloud: CloudType; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  const { score, label, changePct, details } = data;
  const circumference = 2 * Math.PI * 42;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const isHigh = score > 70;
  const isMed = score > 40;
  const badgeColor = isHigh ? "bg-primary/10 text-primary border-primary/20" : isMed ? "bg-sky-500/10 text-sky-500 border-sky-500/20" : "bg-success/10 text-success border-success/20";
  const strokeColor = isHigh ? "hsl(var(--primary))" : isMed ? "hsl(var(--warning, 38 92% 50%))" : "hsl(var(--success, 142 71% 45%))";

  const riskFactors = details?.riskFactors || [];
  const displayedRiskFactors = riskFactors.slice(0, 3);
  const maxRiskFactorCount = Math.max(...displayedRiskFactors.map((item: any) => Number(item?.count ?? 0)), 0);
  const recommendations = details?.recommendations || [];

  return (
    <CardWrapper onClick={onClick} clickable className="overflow-y-auto custom-scrollbar">
      <CardHeader
        icon={Gauge}
        title="Overall Risk Score"
        badge={changePct != null ? (
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${changePct < 0 ? "bg-success/10 text-success" : "bg-primary/10 text-primary"}`}>
            {changePct < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
            {Math.abs(changePct)}%
          </div>
        ) : undefined}
      />

      <div className="flex flex-col items-center justify-center pt-2 pb-4 border-b border-border/10">
        <div className="relative w-40 h-40 drop-shadow-sm hover:scale-[1.02] transition-transform duration-500">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--muted))" strokeWidth="5" className="opacity-20" />
            <circle
              cx="50" cy="50" r="42" fill="none"
              stroke={strokeColor} strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center mt-2">
            <span className="text-5xl font-extrabold text-foreground tracking-tight leading-none">{score}</span>
            <div className={`mt-2 px-3 py-1 rounded-full border text-[11px] font-bold ${badgeColor} backdrop-blur-sm shadow-sm`}>
              {label}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto mt-4 space-y-5">
        {/* Anomaly Metrics Overview */}
        {details?.metrics && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="flex flex-col gap-0.5 p-2 rounded bg-primary/5 border border-primary/10">
              <span className="text-[10px] font-bold text-muted-foreground uppercase">High-Sev Alerts</span>
              <span className="text-[15px] font-extrabold text-primary">{details.metrics.highSeverityAlerts || 0}</span>
            </div>
            <div className="flex flex-col gap-0.5 p-2 rounded bg-sky-500/5 border border-sky-500/10">
              <span className="text-[10px] font-bold text-muted-foreground uppercase">Total Alerts</span>
              <span className="text-[15px] font-extrabold text-sky-500">{details.metrics.totalAlerts || 0}</span>
            </div>
          </div>
        )}
        {/* Risk Factors */}
        {riskFactors.length > 0 && (
          <div>
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-3">Top Risk Drivers</p>
            <div className="space-y-2">
              {displayedRiskFactors.map((item: any) => (
                <div key={item.name} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="font-semibold text-foreground truncate mr-2">{item.name}</span>
                    <span className="text-muted-foreground tabular-nums shrink-0">{item.count} items</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted/30 overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${item.weight > 30 ? "from-primary/80 to-primary" : item.weight > 10 ? "from-sky-500/80 to-sky-500" : "from-blue-500/80 to-blue-500"} transition-all duration-700`}
                      style={{ width: `${getRelativePercentage(item.count, maxRiskFactorCount)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Risky Entities Showcase */}
        {details?.topRiskyNhis && details.topRiskyNhis.length > 0 && (
          <div>
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-3">Top Risky Identities</p>
            <div className="space-y-2">
              {details.topRiskyNhis.slice(0, 3).map((nhi: any, i: number) => (
                <div key={i} className="flex gap-2 p-2 rounded-lg bg-card border border-border/20 shadow-sm relative overflow-hidden group hover:border-border/60 transition-colors">
                  <div className="absolute top-0 bottom-0 left-0 w-1 bg-primary" />
                  <div className="min-w-0 flex-1 pl-1">
                    <div className="text-[12px] font-semibold text-foreground leading-snug truncate" title={nhi.name}>{nhi.name}</div>
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] font-bold">
                       <span className="text-primary bg-primary/10 px-1.5 py-0.5 rounded flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> {nhi.alertCount} Alerts</span>
                       <span className="text-sky-500 bg-sky-500/10 px-1.5 py-0.5 rounded flex items-center gap-1"><Activity className="w-3 h-3" /> {nhi.activityPct}% Load</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CardWrapper>
  );
};

const LifecycleCard = ({ data, onClick }: { data: NhiLifecycleRegistryResponse | null; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  const { totalIdentities, active, retiring, deprecated, serviceAccounts, oauthApps, apiKeys, secrets, identitiesWithoutOwners, sevenDayTrend, details } = data;
  const activeWidth = getRatioPercentage(active, totalIdentities);
  const retiringWidth = getRatioPercentage(retiring, totalIdentities);
  const deprecatedWidth = getRatioPercentage(deprecated, totalIdentities);

  // Build sparkline SVG path
  const trendData = (!sevenDayTrend || sevenDayTrend.length === 0 || sevenDayTrend.every(v => v === 0))
    ? [15, 22, 28, 35, 42, 55, 65]
    : sevenDayTrend;
  const trendMax = Math.max(...trendData, 1);
  const points = trendData.map((v, i) => `${(i / (trendData.length - 1)) * 100},${100 - (v / trendMax) * 80}`).join(" ");
  const areaPath = `M0,100 L${trendData.map((v, i) => `${(i / (trendData.length - 1)) * 100},${100 - (v / trendMax) * 80}`).join(" L")} L100,100 Z`;

  return (
    <CardWrapper onClick={onClick} clickable>
      <CardHeader icon={Users} title="NHI Lifecycle Registry" />

      <div className="flex gap-4">
        {/* Total gauge */}
        <div className="flex-[2] p-5 rounded-2xl bg-muted/10 border border-border/15 flex flex-col justify-center">
          <div className="flex items-center gap-6">
            <div>
              <div className="text-5xl font-extrabold tracking-tight text-foreground tabular-nums">{totalIdentities}</div>
              <div className="text-[15px] font-medium text-muted-foreground mt-1">Total Identities</div>
            </div>
            <div className="flex-1 max-w-[140px]">
              <div className="h-3 rounded-full flex overflow-hidden ring-1 ring-black/[0.03]">
                <div className="bg-gradient-to-r from-emerald-500 to-green-400 transition-all duration-700" style={{ width: `${activeWidth}%` }} />
                <div className="bg-gradient-to-r from-primary to-primary transition-all duration-700" style={{ width: `${retiringWidth}%` }} />
                <div className="bg-gradient-to-r from-red-500 to-rose-400 transition-all duration-700" style={{ width: `${deprecatedWidth}%` }} />
              </div>
              <div className="flex justify-between mt-3 text-[13px] font-medium">
                <span className="flex items-center gap-1.5 text-muted-foreground"><span className="w-2 h-2 rounded-full bg-emerald-500" />Active {active}</span>
                {retiring > 0 && <span className="flex items-center gap-1.5 text-muted-foreground"><span className="w-2 h-2 rounded-full bg-sky-500" />Retiring {retiring}</span>}
                {deprecated > 0 && <span className="flex items-center gap-1.5 text-muted-foreground"><span className="w-2 h-2 rounded-full bg-red-500" />Depr. {deprecated}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Trend line */}
        <div className="flex-1 p-5 rounded-2xl bg-muted/10 border border-border/15 flex flex-col">
          <p className="text-[13px] font-bold text-muted-foreground mb-4">7-Day Trend</p>
          <div className="mt-auto h-[70px] flex items-end relative overflow-hidden group">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
              <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill="url(#trendFill)" />
              <polyline points={points} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            </svg>
            {(!sevenDayTrend || sevenDayTrend.length === 0 || sevenDayTrend.every(v => v === 0)) && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[11px] font-medium text-muted-foreground bg-card/90 px-2 py-0.5 rounded-md shadow-sm border border-border/20">Not available</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto mt-4 space-y-5 custom-scrollbar pr-1">
        {/* Inventory Breakdown */}
        {details?.inventoryBreakdown && details.inventoryBreakdown.length > 0 && (
          <div>
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-3">Inventory</p>
            <div className="grid grid-cols-2 gap-2">
              {details.inventoryBreakdown.map((item: any, i: number) => (
                <div key={i} className="flex justify-between items-center group/inv p-2 rounded bg-card border border-border/40 hover:border-border/80 hover:bg-muted/10 transition-colors">
                  <div className="text-[12px] text-muted-foreground truncate mr-2 group-hover/inv:text-foreground transition-colors" title={item.name}>{item.name}</div>
                  <div className="text-[13px] font-bold text-foreground tabular-nums shrink-0">{item.count}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actionable Dormant Accounts */}
        {details?.topDormant && details.topDormant.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <p className="text-[11px] font-bold text-sky-500 uppercase tracking-widest flex items-center gap-1.5"><Clock className="w-3 h-3" /> Top Dormant</p>
              <div className="text-[11px] font-medium text-muted-foreground">{details.topDormant.length} identified</div>
            </div>
            <div className="space-y-2.5">
              {details.topDormant.slice(0, 6).map((dormant: any, i: number) => (
                <div key={i} className="flex flex-col p-3 rounded-xl bg-sky-500/5 border border-sky-500/20 hover:border-sky-500/50 transition-colors relative overflow-hidden group">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-primary/60" />

                  <div className="flex items-start justify-between gap-3 min-w-0 pl-1">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[12px] font-semibold text-foreground truncate cursor-help" title={dormant.name}>{dormant.name}</span>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px]">
                        <span className="text-primary font-black">{dormant.daysSinceLastActivity} days inactive</span>
                        {!dormant.userManaged && (
                          <span className="text-muted-foreground">Google Managed</span>
                        )}
                      </div>
                    </div>
                    {dormant.highPrivilege && (
                      <div className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 text-[10px] font-bold uppercase mt-0.5">
                        <ShieldAlert className="w-2.5 h-2.5" /> High Priv
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fallback metrics for simple payload */}
        {(!details?.inventoryBreakdown || details.inventoryBreakdown.length === 0) && (!details?.topDormant || details.topDormant.length === 0) && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            {[
              { label: "Service Accounts", value: serviceAccounts, icon: Server, color: "text-blue-500", bg: "bg-blue-500/15 ring-blue-500/10" },
              { label: "API Keys", value: apiKeys, icon: Key, color: "text-sky-500", bg: "bg-sky-500/15 ring-sky-500/10" },
              { label: "OAuth Apps", value: oauthApps, icon: Globe, color: "text-violet-500", bg: "bg-violet-500/15 ring-violet-500/10" },
              { label: "Secrets", value: secrets, icon: FileKey, color: "text-emerald-500", bg: "bg-emerald-500/15 ring-emerald-500/10" },
            ].map((item, i) => (
              <div key={i} className="flex items-center p-3 rounded-xl bg-card border border-border/40">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.bg}`}>
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                </div>
                <div className="ml-3 flex-1">
                  <div className="text-[15px] font-bold text-foreground tabular-nums leading-none">{item.value.toLocaleString()}</div>
                  <div className="text-[11px] font-medium text-muted-foreground mt-0.5 tracking-tight">{item.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {identitiesWithoutOwners > 0 && (
          <div className="p-2.5 rounded-xl bg-primary/5 border border-primary/10 flex items-center gap-2 mt-3 shrink-0">
            <div className="p-1 rounded-md bg-primary/10">
              <AlertTriangle className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-[12px] text-primary font-medium flex-1">{identitiesWithoutOwners} identities without owners</span>
          </div>
        )}

        {/* Aggregate Entity Types to fill space */}
        <div className="mt-auto pt-4 border-t border-border/10">
           <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-3">Entity Types</div>
           <div className="grid grid-cols-4 gap-2">
             <div className="flex flex-col items-center justify-center p-2 rounded-xl bg-muted/10 border border-border/40 hover:bg-muted/30 transition-colors">
               <Server className="w-4 h-4 text-blue-500 mb-1.5" />
               <span className="text-[15px] font-bold text-foreground leading-none">{serviceAccounts}</span>
               <span className="text-[10px] text-muted-foreground mt-1 text-center leading-tight">Service<br/>Accounts</span>
             </div>
             <div className="flex flex-col items-center justify-center p-2 rounded-xl bg-muted/10 border border-border/40 hover:bg-muted/30 transition-colors">
               <Key className="w-4 h-4 text-sky-500 mb-1.5" />
               <span className="text-[15px] font-bold text-foreground leading-none">{apiKeys}</span>
               <span className="text-[10px] text-muted-foreground mt-1 text-center leading-tight">API<br/>Keys</span>
             </div>
             <div className="flex flex-col items-center justify-center p-2 rounded-xl bg-muted/10 border border-border/40 hover:bg-muted/30 transition-colors">
               <Globe className="w-4 h-4 text-violet-500 mb-1.5" />
               <span className="text-[15px] font-bold text-foreground leading-none">{oauthApps}</span>
               <span className="text-[10px] text-muted-foreground mt-1 text-center leading-tight">OAuth<br/>Apps</span>
             </div>
             <div className="flex flex-col items-center justify-center p-2 rounded-xl bg-muted/10 border border-border/40 hover:bg-muted/30 transition-colors">
               <FileKey className="w-4 h-4 text-emerald-500 mb-1.5" />
               <span className="text-[15px] font-bold text-foreground leading-none">{secrets}</span>
               <span className="text-[10px] text-muted-foreground mt-1 text-center leading-tight">Managed<br/>Secrets</span>
             </div>
           </div>
        </div>
      </div>
    </CardWrapper>
  );
};

const RotationCard = ({ data, onClick }: { data: RotationResponse | null; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  const { rotatedPct, pending, details } = data;
  const circumference = 2 * Math.PI * 36;
  const strokeDashoffset = circumference - (rotatedPct / 100) * circumference;
  const isGood = rotatedPct >= 80;

  const metrics = details?.metrics;
  const ageDistribution = details?.ageDistribution || [];
  const recentRotations = details?.recentRotations || [];

  return (
    <CardWrapper className="flex flex-col overflow-y-auto custom-scrollbar" onClick={onClick} clickable>
      <CardHeader icon={RefreshCw} title="Rotation Status" />

      {/* Top: Gauge + Metrics */}
      <div className="flex gap-4 mb-4 mt-2">
        <div className="shrink-0 relative w-24 h-24">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="rotationGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={isGood ? "hsl(142 76% 36%)" : "hsl(38 92% 50%)"} />
                <stop offset="100%" stopColor={isGood ? "hsl(160 84% 39%)" : "hsl(25 95% 53%)"} />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="36" fill="none" stroke="hsl(var(--muted)/0.4)" strokeWidth="5" />
            <circle
              cx="50" cy="50" r="36" fill="none"
              stroke="url(#rotationGrad)" strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-extrabold text-foreground leading-none tracking-tight">{rotatedPct}%</span>
            <span className="text-[10px] text-muted-foreground mt-0.5">Rotated</span>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="flex-1 grid grid-cols-2 gap-2">
          <div className="bg-muted/10 rounded-lg p-2 border border-border/10 flex flex-col justify-center">
            <div className="text-[11px] font-medium text-muted-foreground mb-0.5">Rotated</div>
            <div className="text-lg font-bold text-foreground tabular-nums leading-none">{metrics?.rotated ?? 0}</div>
          </div>
          <div className="bg-sky-500/10 rounded-lg p-2 border border-sky-500/10 flex flex-col justify-center">
            <div className="text-[11px] font-medium text-sky-500 mb-0.5">Pending</div>
            <div className="text-lg font-bold text-sky-500 tabular-nums leading-none">{metrics?.pending ?? pending ?? 0}</div>
          </div>
          <div className="bg-primary/10 rounded-lg p-2 border border-primary/10 flex flex-col justify-center">
            <div className="text-[11px] font-medium text-primary mb-0.5">Failed</div>
            <div className="text-lg font-bold text-primary tabular-nums leading-none">{metrics?.failed ?? 0}</div>
          </div>
          <div className="bg-sky-500/10 rounded-lg p-2 border border-sky-500/10 flex flex-col justify-center hover:bg-sky-500/20 transition-colors">
            <div className="text-[10px] font-medium text-sky-500 mb-0.5 whitespace-nowrap">Avg Age</div>
            <div className="text-lg font-bold text-sky-500 tabular-nums leading-none flex items-baseline">{metrics?.avgDays ?? 0}<span className="text-[11px] ml-0.5 font-normal">d</span></div>
          </div>
        </div>
      </div>

      {/* Age Distribution Horizontal Stack */}
      {ageDistribution.length > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">
            <span>Age Distribution</span>
          </div>
          <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden flex">
            {ageDistribution.map((dist: any, i: number) => {
              const colorClass = i === 0 ? "bg-emerald-500" : i === 1 ? "bg-blue-500" : i === 2 ? "bg-sky-500" : "bg-primary";
              return dist.percentage > 0 ? (
                <div key={i} className={`h-full ${colorClass} transition-all duration-700`} style={{ width: `${dist.percentage}%` }} title={`${dist.range}: ${dist.count}`} />
              ) : null;
            })}
          </div>
          <div className="flex justify-between items-center mt-1.5 text-[10px] text-muted-foreground font-medium">
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />0-30d ({ageDistribution[0]?.percentage || 0}%)</div>
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-primary" />90+d ({ageDistribution[ageDistribution.length - 1]?.percentage || 0}%)</div>
          </div>
        </div>
      )}

      {/* Recent Rotations List */}
      {recentRotations.length > 0 && (
        <div className="flex-1">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-2">Recent Rotations</p>
          <div className="space-y-1.5">
            {recentRotations.slice(0, 3).map((rot: any, i: number) => (
              <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-card border border-border/40 hover:bg-muted/10 transition-colors group">
                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                  <div className="w-6 h-6 rounded-md bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <Key className="w-3 h-3 text-emerald-500" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[12px] font-semibold text-foreground truncate" title={rot.keyId}>{rot.keyId.substring(0, 8)}...</span>
                    <span className="text-[10px] text-muted-foreground truncate" title={rot.serviceAccount}>{rot.serviceAccount.split('@')[0]}</span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[11px] font-bold text-foreground">{rot.ageDays}d</div>
                  <div className="text-[10px] text-muted-foreground">ago</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardWrapper>
  );
};

const CredentialsCard = ({ data, onClick }: { data: StaticCredentialsResponse | null; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  const { totalKeys, agedKeys, rotationRatePct, categories, details } = data;

  const ageDistribution = details?.ageDistribution || [];
  const topAgedKeys = details?.topAgedKeys || [];
  const multipleKeyAccounts = details?.multipleKeyServiceAccounts || [];
  const maxCredentialCategoryTotal = Math.max(...categories.map((item) => Number(item?.total ?? 0)), 0);

  return (
    <CardWrapper className="!h-fit flex flex-col overflow-y-auto custom-scrollbar" onClick={onClick} clickable>
      <CardHeader icon={Key} title="Static Credentials" />

      {/* Hero stat */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-primary/8 to-primary/3 border border-sky-500/10 mb-3 shrink-0">
        <div className="flex items-baseline gap-2 mb-1.5">
          <span className="text-4xl font-extrabold text-foreground leading-none tracking-tight">{totalKeys.toLocaleString()}</span>
          <span className="text-[15px] text-muted-foreground font-medium">keys</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold ring-1 ring-primary/10">
            <AlertTriangle className="w-3 h-3" />{agedKeys} aged
          </span>
          <span className="text-[12px] text-muted-foreground">
            <span className="font-semibold text-foreground">{rotationRatePct}%</span> rotation rate
          </span>
        </div>
      </div>

      {/* Age Distribution Horizontal Stack */}
      {ageDistribution.length > 0 && (
        <div className="mb-4 shrink-0">
          <div className="flex justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">
            <span>Age Distribution</span>
          </div>
          <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden flex">
            {ageDistribution.map((dist: any, i: number) => {
              const colorClass = i === 0 ? "bg-emerald-500" : i === 1 ? "bg-blue-500" : i === 2 ? "bg-sky-500" : "bg-primary";
              return dist.percentage > 0 ? (
                <div key={i} className={`h-full ${colorClass} transition-all duration-700`} style={{ width: `${dist.percentage}%` }} title={`${dist.range}: ${dist.count}`} />
              ) : null;
            })}
          </div>
          <div className="flex justify-between items-center mt-1.5 text-[10px] text-muted-foreground font-medium">
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />0-30d ({ageDistribution[0]?.percentage || 0}%)</div>
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-primary" />90+d ({ageDistribution[ageDistribution.length - 1]?.percentage || 0}%)</div>
          </div>
        </div>
      )}

      {/* Multiple Keys Warning Banner */}
      {multipleKeyAccounts.length > 0 && (
        <div className="p-2.5 rounded-xl bg-primary/5 border border-primary/10 flex items-center gap-2 mb-4 shrink-0">
          <div className="p-1 rounded-md bg-primary/10 shrink-0">
            <AlertTriangle className="w-3.5 h-3.5 text-primary" />
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-[12px] text-primary font-bold">{multipleKeyAccounts.length} accounts have multiple keys!</span>
            <span className="text-[10px] text-primary/80 font-medium truncate">e.g. {multipleKeyAccounts[0].serviceAccount.split('@')[0]} ({multipleKeyAccounts[0].keyCount} keys)</span>
          </div>
        </div>
      )}

      {/* Top Aged Keys List */}
      {topAgedKeys.length > 0 && (
        <div className="mt-auto">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-2">Top Aged Keys</p>
          <div className="space-y-1.5">
            {topAgedKeys.slice(0, 3).map((key: any, i: number) => {
              const isCritical = key.ageDays > 90;
              return (
                <div key={i} className={`flex justify-between items-center p-2 rounded-lg border transition-colors group ${isCritical ? 'bg-primary/5 border-primary/20 hover:border-primary/40' : 'bg-card border-border/40 hover:bg-muted/10'}`}>
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${isCritical ? 'bg-primary/10' : 'bg-muted/20'}`}>
                      <Key className={`w-3 h-3 ${isCritical ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[12px] font-semibold text-foreground truncate" title={key.name}>{key.name}</span>
                      <span className="text-[10px] text-muted-foreground truncate" title={key.serviceAccount}>{key.serviceAccount.split('@')[0]}</span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={`text-[11px] font-bold tabular-nums ${isCritical ? 'text-primary' : 'text-foreground'}`}>{key.ageDays}d</div>
                    <div className="text-[10px] text-muted-foreground">old</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback Credential breakdown */}
      {(!details) && categories && categories.length > 0 && (
        <div className="p-3.5 rounded-xl bg-muted/10 border border-border/15 space-y-3.5 mt-auto">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-2">Credential Breakdown</p>
          <div className="space-y-3">
            {categories.map((item, i) => {
              const colors = ["from-blue-500 to-cyan-500", "from-violet-500 to-purple-500", "from-primary to-orange-500", "from-emerald-500 to-teal-500"];
              return (
                <div key={item.name} className="group">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[15px] font-medium text-muted-foreground group-hover:text-foreground transition-colors">{item.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-bold tabular-nums">{item.total.toLocaleString()}</span>
                      {item.aged > 0 && <span className="text-[13px] text-primary font-semibold">({item.aged})</span>}
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-muted/30 overflow-hidden opacity-80 group-hover:opacity-100 transition-opacity">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${colors[i % colors.length]} transition-all duration-700`}
                      style={{ width: `${getRelativePercentage(item.total, maxCredentialCategoryTotal)}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </CardWrapper>
  );
};

const SecretsCard = ({ data, onClick }: { data: SecretsPostureResponse | null; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  const { totalSecretsManaged, vaultCoveragePct, issuesFound, details } = data;
  const vaultCirc = 2 * Math.PI * 18;
  const vaultOffset = vaultCirc - (vaultCoveragePct / 100) * vaultCirc;

  const vaultDistribution = details?.vaultDistribution || [];
  const topExposedSecrets = details?.topExposedSecrets || [];

  return (
    <CardWrapper className="!h-fit flex flex-col overflow-y-auto custom-scrollbar" onClick={onClick} clickable>
      <CardHeader icon={Lock} title="Secrets Posture" />

      {/* Hero stat with vault gauge and distribution */}
      <div className="p-3.5 rounded-xl bg-gradient-to-br from-primary/8 to-accent/5 border border-sky-500/10 mb-3 shrink-0 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-3xl font-extrabold text-foreground leading-none tracking-tight">{totalSecretsManaged.toLocaleString()}</div>
          <p className="text-[11px] text-muted-foreground mt-1 mb-2">Total Secrets</p>
          {vaultDistribution.map((v: any, i: number) => {
            const isGoogle = v.vault.includes("Google") || v.vault.includes("AWS") || v.vault.includes("Azure");
            const color = isGoogle ? "text-success" : "text-muted-foreground";
            const bgClass = isGoogle ? "bg-success" : "bg-muted-foreground/50";
            return (
              <div key={i} className="flex items-center gap-1.5 mt-0.5">
                <div className={`w-1.5 h-1.5 rounded-full ${bgClass}`} />
                <span className="text-[10px] text-muted-foreground truncate max-w-[120px]" title={v.vault}>{v.vault}</span>
                <span className={`text-[10px] font-bold ${color}`}>{v.percentage}%</span>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col items-center ml-2">
          <div className="relative w-14 h-14 shrink-0">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r="18" fill="none" stroke="hsl(var(--muted)/0.3)" strokeWidth="4" />
              <circle
                cx="22" cy="22" r="18" fill="none"
                stroke="hsl(142 76% 36%)" strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={vaultCirc}
                strokeDashoffset={vaultOffset}
                className="transition-all duration-700"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[12px] font-bold text-foreground">{vaultCoveragePct}%</span>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1 text-center leading-tight">Vault<br />Coverage</p>
        </div>
      </div>

      {/* Issues Grid */}
      <div className="mb-4 shrink-0">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-2">Issues Found</p>
        <div className="grid grid-cols-2 gap-2">
          {issuesFound.map((issue) => {
            const IconComp = issueIconMap[issue.name] || AlertTriangle;
            const isCritical = issue.name.includes("Aged") || issue.name.includes("Outside");
            const isWarning = issue.name.includes("High access") || issue.name.includes("rotation");
            const severityColor = issue.count === 0 ? "text-muted-foreground/40" : isCritical ? "text-primary" : isWarning ? "text-sky-500" : "text-muted-foreground";
            const severityBg = issue.count === 0 ? "bg-muted/10 border-border/10" : isCritical ? "bg-primary/10 border-primary/20" : isWarning ? "bg-sky-500/10 border-sky-500/20" : "bg-muted/20 border-border/20";
            return (
              <div key={issue.name} className={`flex flex-col gap-1.5 p-2 rounded-lg border ${severityBg}`}>
                <div className="flex items-center justify-between">
                  <IconComp className={`w-3.5 h-3.5 ${severityColor}`} />
                  <span className={`text-[15px] font-bold tabular-nums ${severityColor}`}>{issue.count}</span>
                </div>
                <span className={`text-[10px] font-medium leading-tight ${issue.count === 0 ? "text-muted-foreground/50" : "text-foreground"}`}>{issue.name}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Exposed Secrets feed */}
      {topExposedSecrets.length > 0 && (
        <div className="mt-auto">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-2">Top Exposed Secrets</p>
          <div className="space-y-1.5">
            {topExposedSecrets.slice(0, 6).map((secret: any, i: number) => {
              const isCrit = secret.risk === "critical" || secret.riskScore >= 80;
              const isHigh = secret.risk === "high" || (secret.riskScore >= 60 && secret.riskScore < 80);
              const badgeClass = isCrit ? "bg-primary/10 text-primary border-primary/20" : isHigh ? "bg-sky-500/10 text-sky-500 border-sky-500/20" : "bg-muted text-muted-foreground border-border";

              return (
                <div key={i} className={`flex flex-col p-2.5 rounded-xl border transition-colors group ${isCrit ? "bg-primary/5 border-primary/15 hover:border-primary/30" : "bg-card border-border/40 hover:bg-muted/10"}`}>
                  <div className="flex items-start justify-between min-w-0 gap-2 mb-1.5">
                    <span className="text-[12px] font-semibold text-foreground truncate flex-1" title={secret.name}>{secret.name.split('/').pop()}</span>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase ${badgeClass}`}>
                      {secret.risk || "Medium"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="text-muted-foreground bg-muted/30 px-1.5 py-0.5 rounded text-[10px]">{secret.type.substring(0, 10)}</div>
                    <div className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                      <Clock className="w-2.5 h-2.5" /> rotated {secret.lastRotated}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </CardWrapper>
  );
};

const SecretsCredentialsCard = ({ rotation, credentials, secrets, onClick }: {
  rotation: RotationResponse | null;
  credentials: StaticCredentialsResponse | null;
  secrets: SecretsPostureResponse | null;
  onClick?: () => void;
}) => {
  if (!rotation && !credentials && !secrets) return <CardSkeleton />;

  const rotatedPct = rotation?.rotatedPct || 0;
  const pending = rotation?.pending || 0;
  const totalKeys = credentials?.totalKeys || 0;
  const agedKeys = credentials?.agedKeys || 0;
  const totalSecrets = secrets?.totalSecretsManaged || 0;
  const issuesFound = secrets?.issuesFound || [];
  const topAgedKeys = credentials?.details?.topAgedKeys || [];
  const ageDistribution = credentials?.details?.ageDistribution || [];
  const rotationCirc = 2 * Math.PI * 18;
  const rotationOffset = rotationCirc - (rotatedPct / 100) * rotationCirc;
  const isGoodRotation = rotatedPct >= 80;

  return (
    <CardWrapper className="!h-fit flex flex-col overflow-y-auto custom-scrollbar" onClick={onClick} clickable>
      <CardHeader icon={Shield} title="Secrets & Credentials" />

      {/* Top Hero Row: Rotation Gauge + Key Stats */}
      <div className="flex gap-3 mb-3 shrink-0">
        {/* Mini rotation gauge */}
        <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-gradient-to-br from-primary/8 to-primary/3 border border-sky-500/10 shrink-0">
          <div className="relative w-14 h-14">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r="18" fill="none" stroke="hsl(var(--muted)/0.3)" strokeWidth="4" />
              <circle cx="22" cy="22" r="18" fill="none"
                stroke={isGoodRotation ? "hsl(142 76% 36%)" : "hsl(38 92% 50%)"} strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={rotationCirc} strokeDashoffset={rotationOffset}
                className="transition-all duration-700" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[13px] font-extrabold text-foreground leading-none">{rotatedPct}%</span>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1 font-medium">Rotation</p>
        </div>

        {/* Stats grid */}
        <div className="flex-1 grid grid-cols-2 gap-2">
          <div className="bg-sky-500/10 rounded-lg p-2.5 border border-sky-500/10 flex flex-col justify-center">
            <div className="text-[10px] font-medium text-sky-500 mb-0.5">Total Keys</div>
            <div className="text-lg font-bold text-sky-500 tabular-nums leading-none">{totalKeys}</div>
          </div>
          <div className="bg-primary/10 rounded-lg p-2.5 border border-primary/10 flex flex-col justify-center">
            <div className="text-[10px] font-medium text-primary mb-0.5">Aged Keys</div>
            <div className="text-lg font-bold text-primary tabular-nums leading-none">{agedKeys}</div>
          </div>
          <div className="bg-muted/20 rounded-lg p-2.5 border border-border/10 flex flex-col justify-center">
            <div className="text-[10px] font-medium text-muted-foreground mb-0.5">Secrets</div>
            <div className="text-lg font-bold text-foreground tabular-nums leading-none">{totalSecrets}</div>
          </div>
          <div className="bg-sky-500/10 rounded-lg p-2.5 border border-sky-500/10 flex flex-col justify-center">
            <div className="text-[10px] font-medium text-sky-500 mb-0.5">Pending</div>
            <div className="text-lg font-bold text-sky-500 tabular-nums leading-none">{pending}</div>
          </div>
        </div>
      </div>

      {/* Age Distribution */}
      {ageDistribution.length > 0 && (
        <div className="mb-3 shrink-0">
          <div className="flex justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">
            <span>Key Age Distribution</span>
          </div>
          <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden flex">
            {ageDistribution.map((dist: any, i: number) => {
              const colorClass = i === 0 ? "bg-emerald-500" : i === 1 ? "bg-blue-500" : i === 2 ? "bg-sky-500" : "bg-primary";
              return dist.percentage > 0 ? (
                <div key={i} className={`h-full ${colorClass} transition-all duration-700`} style={{ width: `${dist.percentage}%` }} title={`${dist.range}: ${dist.count}`} />
              ) : null;
            })}
          </div>
          <div className="flex justify-between items-center mt-1.5 text-[10px] text-muted-foreground font-medium">
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />0-30d ({ageDistribution[0]?.percentage || 0}%)</div>
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-primary" />90+d ({ageDistribution[ageDistribution.length - 1]?.percentage || 0}%)</div>
          </div>
        </div>
      )}

      {/* Issues Found Compact Grid */}
      {issuesFound.length > 0 && (
        <div className="mb-3 shrink-0">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Issues Found</p>
          <div className="grid grid-cols-2 gap-1.5">
            {issuesFound.map((issue) => {
              const IconComp = issueIconMap[issue.name] || AlertTriangle;
              const isCritical = issue.name.includes("Aged") || issue.name.includes("Outside");
              const isWarning = issue.name.includes("rotation") || issue.name.includes("High");
              const color = issue.count === 0 ? "text-muted-foreground/40" : isCritical ? "text-primary" : isWarning ? "text-sky-500" : "text-muted-foreground";
              const bg = issue.count === 0 ? "bg-muted/10 border-border/10" : isCritical ? "bg-primary/10 border-primary/20" : isWarning ? "bg-sky-500/10 border-sky-500/20" : "bg-muted/20 border-border/20";
              return (
                <div key={issue.name} className={`flex items-center gap-2 p-1.5 rounded-lg border ${bg}`}>
                  <IconComp className={`w-3 h-3 ${color} shrink-0`} />
                  <span className={`text-[10px] font-medium leading-tight truncate ${issue.count === 0 ? "text-muted-foreground/50" : "text-foreground"}`}>{issue.name}</span>
                  <span className={`text-[11px] font-bold tabular-nums ml-auto ${color}`}>{issue.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top 3 Aged Keys */}
      {topAgedKeys.length > 0 && (
        <div className="mt-auto">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-2">Top Aged Keys</p>
          <div className="space-y-1.5">
            {topAgedKeys.slice(0, 3).map((key: any, i: number) => {
              const isCritical = key.ageDays > 90;
              return (
                <div key={i} className={`flex justify-between items-center p-2 rounded-lg border transition-colors group ${isCritical ? 'bg-primary/5 border-primary/20' : 'bg-card border-border/40'}`}>
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${isCritical ? 'bg-primary/10' : 'bg-muted/20'}`}>
                      <Key className={`w-3 h-3 ${isCritical ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[12px] font-semibold text-foreground truncate" title={key.name}>{key.name.substring(0, 12)}...</span>
                      <span className="text-[10px] text-muted-foreground truncate" title={key.serviceAccount}>{key.serviceAccount.split('@')[0]}</span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={`text-[11px] font-bold tabular-nums ${isCritical ? 'text-primary' : 'text-foreground'}`}>{key.ageDays}d</div>
                    <div className="text-[10px] text-muted-foreground">old</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </CardWrapper>
  );
};

const HumanCard = ({ data, onClick }: { data: NhiReviewResponse | null; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  const { requiringImmediateAttention, verifiedNhi, underReview, topNhiRequiringReview, details } = data;

  const reviewStats = details?.reviewStats || [];
  const reviewQueue = details?.reviewQueue || [];

  return (
    <CardWrapper className="!h-fit flex flex-col overflow-y-auto custom-scrollbar" onClick={onClick} clickable>
      <CardHeader
        icon={UserCheck}
        title="NHI Review"
        badge={
          requiringImmediateAttention > 0 ? (
            <div className="px-2 py-0.5 rounded-full bg-primary/10 ring-1 ring-primary/15 flex items-center gap-1.5 shrink-0">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-[11px] font-bold text-primary whitespace-nowrap">{requiringImmediateAttention} urgent</span>
            </div>
          ) : null
        }
      />

      {/* Review Stats Grid */}
      {reviewStats.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 mb-4 shrink-0 mt-2">
          {reviewStats.map((stat: any, i: number) => {
            const isHighlight = stat.label.includes("Compliance Rate") || stat.label.includes("Urgent");
            const valColor = stat.label.includes("Compliance") ? "text-success" : stat.label.includes("Urgent") && stat.value > 0 ? "text-primary" : "text-foreground";
            return (
              <div key={i} className={`p-2 rounded-lg border flex flex-col justify-center ${isHighlight ? 'bg-sky-500/5 border-sky-500/10' : 'bg-muted/10 border-border/10'}`}>
                <div className="text-[11px] font-medium text-muted-foreground mb-0.5">{stat.label}</div>
                <div className={`text-lg font-bold tabular-nums leading-none ${valColor}`}>{stat.value}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 mb-3 shrink-0 mt-2">
          {/* Fallback to old stat pills */}
          <div className="p-3 rounded-xl bg-sky-500/5 border border-sky-500/10 text-center">
            <div className="p-1.5 rounded-lg bg-sky-500/10 w-fit mx-auto mb-1.5">
              <Eye className="w-4 h-4 text-sky-500" />
            </div>
            <div className="text-xl font-extrabold text-foreground tabular-nums">{underReview}</div>
            <div className="text-[11px] text-muted-foreground font-medium">Under Review</div>
          </div>
          <div className="p-3 rounded-xl bg-success/5 border border-success/10 text-center">
            <div className="p-1.5 rounded-lg bg-success/10 w-fit mx-auto mb-1.5">
              <CheckCircle className="w-4 h-4 text-success" />
            </div>
            <div className="text-xl font-extrabold text-foreground tabular-nums">{verifiedNhi.toLocaleString()}</div>
            <div className="text-[11px] text-muted-foreground font-medium">Verified NHI</div>
          </div>
        </div>
      )}

      {/* Review Queue List */}
      <div className="mt-auto">
        <div className="flex justify-between items-center mb-2">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Review Queue</p>
          {reviewQueue.length > 0 && <span className="text-[11px] font-semibold text-muted-foreground">{reviewQueue.length} items</span>}
        </div>

        {reviewQueue.length > 0 ? (
          <div className="space-y-2">
            {reviewQueue.slice(0, 3).map((item: any, i: number) => {
              const isCrit = item.status === "critical";
              const isHigh = item.status === "high";
              const badgeClass = isCrit ? "bg-primary/10 text-primary border-primary/20" : isHigh ? "bg-sky-500/10 text-sky-500 border-sky-500/20" : "bg-muted text-muted-foreground border-border";

              return (
                <div key={i} className={`flex flex-col p-2.5 rounded-xl border transition-colors group ${isCrit ? "bg-primary/5 border-primary/15 hover:border-primary/30" : "bg-card border-border/40 hover:bg-muted/10"}`}>
                  <div className="flex items-start justify-between min-w-0 gap-2 mb-1.5">
                    <span className="text-[12px] font-semibold text-foreground truncate flex-1" title={item.name}>{item.name.split('@')[0]}</span>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase ${badgeClass}`}>
                      {item.status}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mb-2" title={item.reason}>{item.reason}</div>
                  <div className="flex items-center justify-between text-[10px] font-medium mt-auto pt-2 border-t border-border/40">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Clock className="w-3 h-3" /> Due {item.dueDate}
                    </div>
                    <div className="text-foreground bg-muted/30 px-1.5 py-0.5 rounded">{item.assignee}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-1.5">
            {/* Fallback old top NHIs list */}
            {topNhiRequiringReview.map((nhi, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-card/50 border border-border/10 hover:border-border/30 transition-all group">
                <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 text-[11px] font-bold ${i === 0 ? "bg-primary/15 text-primary" : i === 1 ? "bg-sky-500/15 text-sky-500" : "bg-muted/40 text-muted-foreground"}`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold truncate text-foreground">{nhi.nhiName}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{nhi.reason}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-8 h-1.5 rounded-full bg-muted/30 overflow-hidden">
                    <div className={`h-full rounded-full ${nhi.score > 80 ? "bg-primary" : nhi.score > 50 ? "bg-sky-500" : "bg-success"}`} style={{ width: `${nhi.score}%` }} />
                  </div>
                  <span className={`text-[12px] font-bold tabular-nums ${nhi.score > 80 ? "text-primary" : nhi.score > 50 ? "text-sky-500" : "text-success"}`}>{nhi.score}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CardWrapper>
  );
};

interface SaasAppInfo {
  name: string;
  integrations: number;
  secrets: number;
  status: string;
}

const CorrelationsCard = ({ data, saasApps, onClick }: { data: NhiCorrelationsResponse | null; saasApps: SaasAppInfo[]; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  const { totalRelations, avgSecretsPerNhi, avgNhisPerService, highRiskLinks, correlations, saasIntegrations, details } = data;

  const stats = details?.stats || { uniqueEntities: 0, uniqueSecrets: 0, saasPlatforms: 0 };

  // Calculate local counts directly from full correlations array to ensure list accuracy
  const uniqueEntitiesCount = new Set(correlations.map(c => c.entity)).size;
  const uniqueSecretsSet = new Set<string>();
  correlations.forEach(c => {
    (c.entityAssociated || []).forEach(s => uniqueSecretsSet.add(s));
  });
  const uniqueSecretsCount = uniqueSecretsSet.size;

  const normalizedSaasIntegrations: any[] = (saasIntegrations || [])
    .filter((app: any) => Boolean(app.name) && app.name.toLowerCase() !== 'unknown')
    .map((app: any) => ({
      name: app.name,
      integrations: Number(app.meta?.integrations ?? 0),
      secrets: Number(app.meta?.secrets ?? 0),
      status: app.status || 'connected',
      risk: app.risk || 'Low',
    }));

  const combinedSaasAppsMap = new Map<string, any>();
  saasApps.forEach(app => combinedSaasAppsMap.set(app.name.toLowerCase(), { ...app, risk: 'Low' }));
  normalizedSaasIntegrations.forEach(app => {
    const existing = combinedSaasAppsMap.get(app.name.toLowerCase());
    if (existing) {
      existing.secrets = Math.max(existing.secrets, app.secrets);
      existing.integrations = Math.max(existing.integrations, app.integrations);
      existing.status = app.status;
      existing.risk = app.risk;
    } else {
      combinedSaasAppsMap.set(app.name.toLowerCase(), app);
    }
  });
  const displaySaasApps: any[] = Array.from(combinedSaasAppsMap.values());

  const highCount = correlations.filter(c => c.severity?.toLowerCase() === 'high' || c.severity?.toLowerCase() === 'critical').length;
  const mediumCount = correlations.filter(c => c.severity?.toLowerCase() === 'medium').length;
  const lowCount = correlations.filter(c => c.severity?.toLowerCase() === 'low').length;
  const correlationSeverityTotal = highCount + mediumCount + lowCount;
  const highRiskCount = highCount + (highRiskLinks || 0);
  const maxCorrelationAverage = Math.max(Number(avgSecretsPerNhi ?? 0), Number(avgNhisPerService ?? 0), 0);

  return (
    <CardWrapper className="flex flex-col h-full" onClick={onClick} clickable>
      <CardHeader
        icon={Network}
        title="NHI Correlations"
        badge={totalRelations > 0 ? (
          <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-primary/10 text-primary font-bold ring-1 ring-primary/15">
            {highRiskLinks} High Risk Links
          </span>
        ) : undefined}
      />

      {/* Severity heat strip */}
      {(highCount > 0 || mediumCount > 0 || lowCount > 0) && (
        <div className="mb-4 shrink-0 mt-2">
          <div className="h-2 rounded-full bg-muted/20 overflow-hidden flex">
            {highCount > 0 && <div className="bg-gradient-to-r from-primary to-sky-500 transition-all duration-700" style={{ width: `${getRatioPercentage(highCount, correlationSeverityTotal)}%` }} />}
            {mediumCount > 0 && <div className="bg-gradient-to-r from-sky-400 to-sky-200 transition-all duration-700" style={{ width: `${getRatioPercentage(mediumCount, correlationSeverityTotal)}%` }} />}
            {lowCount > 0 && <div className="bg-gradient-to-r from-emerald-500 to-green-400 transition-all duration-700" style={{ width: `${getRatioPercentage(lowCount, correlationSeverityTotal)}%` }} />}
          </div>
        </div>
      )}

      {/* Stats row directly from Details.stats API */}
      <div className="grid grid-cols-4 gap-2 mb-4 shrink-0">
        {[
          { label: "Entities", value: uniqueEntitiesCount, icon: UserCheck, color: "text-blue-500", bg: "bg-blue-500/10 border-blue-500/15" },
          { label: "Secrets", value: uniqueSecretsCount, icon: Lock, color: "text-sky-500", bg: "bg-sky-500/10 border-sky-500/15" },
          { label: "SaaS Apps", value: displaySaasApps.length, icon: Globe, color: "text-emerald-500", bg: "bg-emerald-500/10 border-emerald-500/15" },
          { label: "Relations", value: totalRelations, icon: Link, color: "text-sky-500", bg: "bg-sky-500/10 border-sky-500/15" },
        ].map(s => (
          <div key={s.label} className={`p-2 rounded-xl flex items-center justify-between border ${s.bg}`}>
            <div className="flex flex-col">
              <span className={`text-[12px] font-semibold text-foreground`}>{s.value}</span>
              <span className="text-[10px] font-medium text-muted-foreground">{s.label}</span>
            </div>
            <s.icon className={`w-3.5 h-3.5 opacity-80 ${s.color}`} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
        {/* Col 1: SaaS Apps Nodes */}
        <div className="flex flex-col h-full bg-muted/10 rounded-xl border border-border/15 p-3 overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5"><Globe className="w-3 h-3 text-sky-500" /> SaaS Integrations</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-card text-muted-foreground border font-bold">{displaySaasApps.length}</span>
          </div>
          <div className="space-y-1.5 overflow-y-auto custom-scrollbar pr-1">
            {displaySaasApps.map((app, i) => {
              const isHighRisk = app.risk?.toLowerCase() === 'high';
              const riskColor = isHighRisk ? 'text-primary' : 'text-emerald-500';
              const riskBg = isHighRisk ? 'bg-primary/10 ring-primary/15' : 'bg-emerald-500/10 ring-emerald-500/15';
              return (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-card border border-border/40 hover:bg-muted/10 transition-colors group">
                  <div className={`p-1.5 rounded-md ${riskBg} shrink-0`}>
                    <Globe className={`w-3.5 h-3.5 ${riskColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-bold truncate text-foreground flex items-center gap-1">
                      {app.name}
                      {app.status === 'unknown' ? <span className="w-1.5 h-1.5 rounded-full bg-sky-500 mx-1" /> : <span className="w-1.5 h-1.5 rounded-full bg-success mx-1" />}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate">{app.secrets} bound secrets</div>
                  </div>
                  <div className={`text-[10px] px-1.5 py-0.5 rounded-md border font-extrabold uppercase shrink-0 ${riskBg} ${riskColor}`}>
                    {app.risk} Risk
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Col 2: Top NHIs */}
        <div className="flex flex-col h-full bg-muted/10 rounded-xl border border-border/15 p-3 overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5"><Users className="w-3 h-3 text-sky-500" /> Top NHIs</span>
          </div>
          <div className="space-y-1.5 overflow-y-auto custom-scrollbar pr-1">
            {(details?.topNhis || []).slice(0, 4).map((nhi: any, i: number) => (
              <div key={i} className="flex flex-col gap-1 p-2 rounded-lg bg-card border border-border/40 transition-colors">
                 <div className="text-[12px] font-bold text-foreground truncate" title={nhi.name}>{nhi.name}</div>
                 <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-bold">{nhi.correlationCount} Links</span>
                    <span className="text-[10px] text-muted-foreground">{nhi.riskScore}% Risk</span>
                 </div>
              </div>
            ))}
          </div>
        </div>

        {/* Col 3: Top Secrets */}
        <div className="flex flex-col h-full bg-muted/10 rounded-xl border border-border/15 p-3 overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5"><Key className="w-3 h-3 text-sky-500" /> Top Secrets</span>
          </div>
          <div className="space-y-1.5 overflow-y-auto custom-scrollbar pr-1">
            {(details?.topSecrets || []).slice(0, 4).map((sec: any, i: number) => (
              <div key={i} className="flex flex-col gap-1 p-2 rounded-lg bg-card border border-border/40 transition-colors">
                 <div className="text-[12px] font-bold text-foreground truncate" title={sec.name}>{sec.name}</div>
                 <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] bg-sky-500/10 text-sky-500 px-1.5 py-0.5 rounded font-bold">{sec.correlationCount} Links</span>
                    <span className="text-[10px] text-muted-foreground truncate" title={sec.type}>{sec.type?.replace(/_/g, ' ')}</span>
                 </div>
              </div>
            ))}
          </div>
        </div>

        {/* Col 4: Entity -> Secrets */}
        <div className="flex flex-col h-full bg-muted/10 rounded-xl border border-border/15 p-3 overflow-hidden">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5"><Network className="w-3 h-3 text-sky-500" /> Entity → Secrets</span>
          </div>
          <div className="space-y-2 overflow-y-auto custom-scrollbar pr-1">
            {correlations.slice(0, 4).map((item) => {
              const severity = item.severity?.toLowerCase();
              const isCrit = severity === 'critical' || severity === 'high';
              const color = isCrit ? 'text-primary' : severity === 'medium' ? 'text-primary' : 'text-emerald-500';
              const bg = isCrit ? 'bg-primary/10' : severity === 'medium' ? 'bg-sky-500/10' : 'bg-emerald-500/10';

              return (
                <div key={item.id} className="p-2 rounded-lg bg-card border border-border/40 hover:border-sky-500/20 transition-all shadow-sm group">
                  <div className="flex items-center gap-2 mb-1.5 border-b border-border/30 pb-1.5">
                    <div className={`w-4 h-4 rounded-md flex items-center justify-center shrink-0 ${bg}`}>
                      <AlertTriangle className={`w-2.5 h-2.5 ${color}`} />
                    </div>
                    <span className="text-[12px] font-bold text-foreground truncate w-full" title={item.entity}>{item.entity}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(item.entityAssociated || []).slice(0, 2).map((secret, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/30 text-muted-foreground border border-border/40 flex items-center gap-1 font-medium truncate max-w-full" title={secret}>
                        <Key className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">{secret}</span>
                      </span>
                    ))}
                    {(item.entityAssociated?.length || 0) > 2 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded text-muted-foreground font-medium border border-transparent">
                        +{(item.entityAssociated.length - 2)}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Bottom: Risk Summary + Insights */}
      <div className="mt-4 pt-4 border-t border-border/10 shrink-0">
        <div className="grid grid-cols-3 gap-3">
          {/* Severity breakdown chips */}
          <div className="flex flex-col gap-1.5 p-2.5 rounded-xl bg-primary/5 border border-primary/15">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">High Risk</span>
              <span className="text-[15px] font-black text-primary tabular-nums">{highRiskCount}</span>
            </div>
            <div className="h-1 rounded-full bg-primary/20 overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${getRatioPercentage(highRiskCount, totalRelations)}%` }} />
            </div>
            <span className="text-[10px] text-muted-foreground">links to address immediately</span>
          </div>

          <div className="flex flex-col gap-1.5 p-2.5 rounded-xl bg-sky-500/5 border border-sky-500/15">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Medium</span>
              <span className="text-[15px] font-black text-sky-500 dark:text-primary tabular-nums">{mediumCount}</span>
            </div>
            <div className="h-1 rounded-full bg-sky-500/20 overflow-hidden">
              <div className="h-full bg-sky-500 rounded-full transition-all duration-700" style={{ width: `${getRatioPercentage(mediumCount, totalRelations)}%` }} />
            </div>
            <span className="text-[10px] text-muted-foreground">for scheduled review</span>
          </div>

          <div className="flex flex-col gap-1.5 p-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Avg Secrets</span>
              <span className="text-[15px] font-black text-emerald-600 dark:text-emerald-500 tabular-nums">{typeof avgSecretsPerNhi === 'number' ? avgSecretsPerNhi.toFixed(1) : '0.0'}</span>
            </div>
            <div className="h-1 rounded-full bg-emerald-500/20 overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all duration-700" style={{ width: `${getRelativePercentage(avgSecretsPerNhi, maxCorrelationAverage)}%` }} />
            </div>
            <span className="text-[10px] text-muted-foreground">per NHI identity</span>
          </div>
        </div>

        {/* Actionable insight banner */}
        {highRiskLinks > 0 && (
          <div className="mt-3 flex items-start gap-2.5 p-2.5 rounded-xl bg-primary/5 border border-primary/15">
            <ShieldAlert className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div className="flex flex-col text-[13px]">
              <span className="font-bold text-primary">Immediate Action Required</span>
              <span className="text-muted-foreground text-[11px] mt-0.5 leading-snug">
                {highRiskLinks} high-risk correlation{highRiskLinks > 1 ? 's' : ''} detected across {displaySaasApps.length} SaaS platform{displaySaasApps.length > 1 ? 's' : ''}. Review entity access paths and rotate exposed secrets.
              </span>
            </div>
          </div>
        )}
      </div>
    </CardWrapper>
  );
};

// ==================== QUICKSTATS REFACTORED TO CARDS ====================

const TotalNhisCard = ({ data, onClick }: { data: TotalNhisResponse | null; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  const alertTrend = data.details?.alertTrend || [];
  const trendMax = Math.max(...alertTrend, 1);
  const trendPath = alertTrend.length > 0
    ? `M0,100 L${alertTrend.map((v: number, i: number) => `${(i / (alertTrend.length - 1)) * 100},${100 - (v / trendMax) * 80}`).join(" L")} L100,100 Z`
    : "";

  return (
    <CardWrapper className="!h-fit flex flex-col overflow-y-auto custom-scrollbar group relative p-6" onClick={onClick} clickable>
      <div className="absolute inset-0 bg-gradient-to-br from-sky-500/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      <div className="relative flex justify-between items-start mb-6 shrink-0">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-sky-500/10 ring-1 ring-sky-500/20">
              <Users className="w-4 h-4 text-sky-500" />
            </div>
            <h3 className="font-bold text-[15px] text-foreground tracking-tight">Total NHIs</h3>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black tracking-tighter text-foreground">
              {data.totalNhis?.toLocaleString()}
            </span>
            {data.changePct != null && (
              <span className={`text-[13px] font-bold px-1.5 py-0.5 rounded-md ${data.changePct < 0 ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary'}`}>
                {data.changePct > 0 ? '+' : ''}{data.changePct}%
              </span>
            )}
          </div>
        </div>
        {/* Trend Sparkline */}
        {alertTrend.length > 0 && (
          <div className="w-24 h-12 opacity-80 group-hover:opacity-100 transition-opacity">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full overflow-visible">
              <defs>
                <linearGradient id="nhiTrendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={trendPath} fill="url(#nhiTrendGrad)" />
              <polyline
                points={alertTrend.map((v: number, i: number) => `${(i / (alertTrend.length - 1)) * 100},${100 - (v / trendMax) * 80}`).join(" ")}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 shrink-0">
        <div className="space-y-2 relative before:absolute before:inset-y-0 before:-right-2 before:w-px before:bg-border/50">
          <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Inventory</div>
          {data.details?.inventoryBreakdown?.slice(0, 3).map((item: any) => (
            <div key={item.name} className="flex justify-between items-center text-[13px]">
              <div className="truncate mr-2 text-muted-foreground font-medium">{item.name}</div>
              <div className="font-bold text-foreground tabular-nums">{item.count}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2 pl-2">
          <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Risk Signals</div>
          {data.details?.summary && (
            <div className="flex flex-col gap-1.5">
              {(data.details.summary.highPrivilegeServiceAccounts > 0) && (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20 text-[11px] font-bold w-fit">
                  <ShieldAlert className="w-3 h-3" />
                  {data.details.summary.highPrivilegeServiceAccounts} High Privilege
                </div>
              )}
              {(data.details.summary.dormantServiceAccounts > 0) && (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-sky-500/10 text-sky-500 border border-sky-500/20 text-[11px] font-bold w-fit">
                  <Clock className="w-3 h-3" />
                  {data.details.summary.dormantServiceAccounts} Dormant
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {data.details?.topRiskyNhis && data.details.topRiskyNhis.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border/10">
          <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Top Risky NHIs</div>
          <div className="space-y-1.5 flex flex-col">
            {data.details.topRiskyNhis.slice(0, 4).map((nhi: any, idx: number) => (
              <div key={idx} className="flex flex-col gap-1.5 text-[13px] p-2 rounded-lg bg-muted/10 border border-border/5 hover:bg-muted/30 transition-colors">
                <div className="flex justify-between items-center w-full">
                  <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                     <div className="w-1.5 h-1.5 rounded-full mt-0.5 shrink-0 bg-primary" />
                     <span className="font-semibold text-foreground truncate">{nhi.name || nhi.nhiName}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mt-0.5 w-full">
                  <div className="flex flex-col gap-1 w-full">
                    <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1 font-semibold text-primary/80"><ShieldAlert className="w-2.5 h-2.5" /> Alerts</span>
                      <span className="font-bold text-foreground">{nhi.alertPct}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-primary/10 rounded-full overflow-hidden flex">
                       <div className="h-full bg-primary" style={{ width: `${nhi.alertPct || 0}%` }} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                     <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1 font-semibold text-sky-500/80"><Activity className="w-2.5 h-2.5" /> Activity</span>
                      <span className="font-bold text-foreground">{nhi.activityPct}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-sky-500/10 rounded-full overflow-hidden flex">
                       <div className="h-full bg-sky-500" style={{ width: `${nhi.activityPct || 0}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardWrapper>
  );
};

const ActiveSecretsCard = ({ data, onClick }: { data: ActiveSecretsResponse | null; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  return (
    <CardWrapper className="!h-fit flex flex-col overflow-y-auto custom-scrollbar group relative p-6" onClick={onClick} clickable>
      <div className="absolute inset-0 bg-gradient-to-br from-sky-500/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      <div className="relative flex justify-between items-start mb-6 shrink-0">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-sky-500/10 ring-1 ring-sky-500/20">
              <Key className="w-4 h-4 text-sky-500" />
            </div>
            <h3 className="font-bold text-[15px] text-foreground tracking-tight">Total Secrets</h3>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black tracking-tighter text-foreground">
              {data.activeSecrets?.toLocaleString()}
            </span>
          </div>
        </div>
        {data.details?.summary && (
          <div className="relative w-14 h-14 shrink-0 mt-1">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="none" className="stroke-muted/30" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="16" fill="none"
                className="stroke-success" strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${((data.details.summary.rotationEnabledSecrets || 0) / Math.max(1, data.details.summary.totalAcrossStores || 1)) * 100} 100`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <span className="text-[11px] font-bold text-success leading-none">
                {Math.round(((data.details.summary.rotationEnabledSecrets || 0) / Math.max(1, data.details.summary.totalAcrossStores || 1)) * 100)}%
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2 shrink-0">
        <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Vaulting</div>
        <div className="grid grid-cols-2 gap-2">
          {data.details?.inventoryByType?.map((item: any) => (
            <div key={item.name} className="flex items-center gap-2 text-[13px] bg-muted/10 p-2 rounded-lg border border-border/10">
              <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />
              <div className="truncate mr-2 text-muted-foreground font-medium flex-1">{item.name}</div>
              <div className="font-bold text-foreground tabular-nums">{item.count}</div>
            </div>
          ))}
        </div>
      </div>

      {data.details?.topRiskySecrets && data.details.topRiskySecrets.length > 0 ? (
        <div className="mt-4 pt-4 border-t border-border/10">
          <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Top Risky Secrets</div>
          <div className="space-y-1.5 flex flex-col">
            {data.details.topRiskySecrets.slice(0, 4).map((secret: any, idx: number) => (
              <div key={idx} className="flex flex-col gap-1.5 text-[13px] p-2 rounded-lg bg-muted/10 border border-border/5 hover:bg-muted/30 transition-colors">
                <div className="flex justify-between items-center w-full">
                  <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                    <div className="w-1.5 h-1.5 rounded-full mt-0.5 shrink-0 bg-primary" />
                    <span className="font-semibold text-foreground truncate">{secret.name || secret.secretName}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-0.5 w-full">
                  <div className="flex flex-col gap-1 w-full">
                    <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1 font-semibold text-primary/80"><ShieldAlert className="w-2.5 h-2.5" /> Alerts</span>
                      <span className="font-bold text-foreground">{secret.alertPct}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-primary/10 rounded-full overflow-hidden flex">
                       <div className="h-full bg-primary" style={{ width: `${secret.alertPct || 0}%` }} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                     <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1 font-semibold text-sky-500/80"><Activity className="w-2.5 h-2.5" /> Activity</span>
                      <span className="font-bold text-foreground">{secret.activityPct}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-sky-500/10 rounded-full overflow-hidden flex">
                       <div className="h-full bg-sky-500" style={{ width: `${secret.activityPct || 0}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : data.details?.summary ? (
        <div className="mt-4 pt-4 border-t border-border/10 flex flex-col justify-end">
          <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Key Insights</div>
          <div className="space-y-2">
            {data.details.summary.outsideVaultSecrets > 0 && (
              <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-primary/5 border border-primary/15">
                <AlertTriangle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="flex flex-col text-[13px]">
                  <span className="font-bold text-primary">High Exposure</span>
                  <span className="text-muted-foreground text-[11px] mt-0.5 leading-snug">There are {data.details.summary.outsideVaultSecrets} secrets living completely outside of managed vaults. This poses a significant operational risk.</span>
                </div>
              </div>
            )}
            {data.details.summary.rotationEnabledSecrets < data.details.summary.totalAcrossStores && (
              <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-sky-500/5 border border-sky-500/15">
                <RefreshCw className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" />
                <div className="flex flex-col text-[13px]">
                  <span className="font-bold text-sky-500 dark:text-sky-500">Limited Automation</span>
                  <span className="text-muted-foreground text-[11px] mt-0.5 leading-snug">Only {Math.round(((data.details.summary.rotationEnabledSecrets || 0) / Math.max(1, data.details.summary.totalAcrossStores || 1)) * 100)}% of tracked secrets have automated rotation policies enabled in the cloud.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </CardWrapper>
  );
};

const ReviewAgedNhisCard = ({ data, onClick }: { data: CriticalRisksResponse | null; onClick?: () => void }) => {
  if (!data) return <CardSkeleton />;
  return (
    <CardWrapper className="!h-fit flex flex-col overflow-y-auto custom-scrollbar group relative p-6" onClick={onClick} clickable>
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      <div className="relative flex justify-between items-start mb-4 shrink-0">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-primary/10 ring-1 ring-primary/20">
              <AlertTriangle className="w-4 h-4 text-primary" />
            </div>
            <h3 className="font-bold text-[15px] text-foreground tracking-tight">Review Aged NHIs</h3>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black tracking-tighter text-foreground">
              {data.criticalRisks?.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Top Offender</div>
          {data.details?.topFindings?.[0] ? (
            <div className="text-[11px] px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20 font-bold mix-blend-multiply dark:mix-blend-lighten">
              {data.details.topFindings[0].ageDays}d Aged
            </div>
          ) : null}
        </div>
      </div>

      <div className="space-y-2.5 mt-2">
        <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Top Review Candidates</div>
        {data.details?.reviewCandidates?.slice(0, 3).map((candidate: any, idx: number) => (
          <div key={idx} className="flex flex-col gap-1.5 text-[13px] p-2 rounded-lg bg-muted/10 border border-border/5 hover:bg-muted/30 transition-colors">
            <div className="flex justify-between items-center w-full">
              <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                <div className="w-1.5 h-1.5 rounded-full mt-0.5 shrink-0 bg-primary" />
                <span className="font-semibold text-foreground truncate" title={candidate.name}>{candidate.name}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-0.5 w-full">
              <div className="flex flex-col gap-1 w-full">
                <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1 font-semibold text-primary/80"><ShieldAlert className="w-2.5 h-2.5" /> Alerts</span>
                  <span className="font-bold text-foreground">{candidate.alertPct}%</span>
                </div>
                <div className="h-1.5 w-full bg-primary/10 rounded-full overflow-hidden flex">
                   <div className="h-full bg-primary" style={{ width: `${candidate.alertPct || 0}%` }} />
                </div>
              </div>
              <div className="flex flex-col gap-1 w-full">
                 <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1 font-semibold text-sky-500/80"><Activity className="w-2.5 h-2.5" /> Activity</span>
                  <span className="font-bold text-foreground">{candidate.activityPct}%</span>
                </div>
                <div className="h-1.5 w-full bg-sky-500/10 rounded-full overflow-hidden flex">
                   <div className="h-full bg-sky-500" style={{ width: `${candidate.activityPct || 0}%` }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </CardWrapper>
  );
};

// Card renderer
const renderCard = (id: string, dashboardData: DashboardData, onCardClick?: (cardId: string) => void, cloud?: CloudType, saasApps?: SaasAppInfo[]) => {
  const handleClick = () => onCardClick?.(id);

  switch (id) {
    case "total-nhis": return <TotalNhisCard data={dashboardData.totalNhis} onClick={handleClick} />;
    case "total-secrets": return <ActiveSecretsCard data={dashboardData.activeSecrets} onClick={handleClick} />;
    case "review-aged-nhis": return <ReviewAgedNhisCard data={dashboardData.criticalRisks} onClick={handleClick} />;
    case "riskScore": return <RiskScoreCard data={dashboardData.riskScore} selectedCloud={cloud || "GCP"} onClick={handleClick} />;
    case "lifecycle": return <LifecycleCard data={dashboardData.lifecycle} onClick={handleClick} />;
    case "secretsCredentials": return <SecretsCredentialsCard rotation={dashboardData.rotation} credentials={dashboardData.credentials} secrets={dashboardData.secrets} onClick={handleClick} />;
    case "correlations": return <CorrelationsCard data={dashboardData.correlations} saasApps={saasApps || []} onClick={handleClick} />;
    default: return null;
  }
};

// Expanded content renderer
const renderExpandedCard = (id: string, dashboardData: DashboardData, selectedCloud: CloudType, cloudSaasCorrelations?: CloudSaasCorrelation[]) => {
  switch (id) {
    case "riskScore": {
      const d = dashboardData.riskScore;
      if (!d) return null;
      return <ExpandedRiskScoreCard data={{
        cloudProvider: selectedCloud.toLowerCase(),
        score: d.score,
        level: d.label.replace(" Risk", ""),
        breakdown: d.details?.breakdown || [
          { category: "Access", score: d.access, weight: 50 },
          { category: "Secrets", score: d.secrets, weight: 50 },
        ],
        trend: "improving",
        delta: d.changePct ?? 0,
        riskFactors: d.details?.riskFactors || [],
        recommendations: d.details?.recommendations || [],
        metrics: d.details?.metrics || {},
        severityBreakdown: d.details?.severityBreakdown || { critical: 0, high: 0, medium: 0, low: 0 },
        topRiskyNhis: d.details?.topRiskyNhis || [],
        topRiskySecrets: d.details?.topRiskySecrets || [],
        recentAlerts: d.details?.recentAlerts || []
      }} />;
    }
    case "lifecycle": {
      const d = dashboardData.lifecycle;
      if (!d) return null;
      return <ExpandedLifecycleCard data={{
        stats: { total: d.totalIdentities, active: d.active, retiring: d.retiring, deprecated: d.deprecated },
        trend: d.sevenDayTrend || [],
        categories: [
          { name: "Service Accounts", count: d.serviceAccounts, icon: Server },
          { name: "API Keys", count: d.apiKeys, icon: Key },
          { name: "OAuth Apps", count: d.oauthApps, icon: Globe },
          { name: "Secrets", count: d.secrets, icon: FileKey },
        ],
        alert: d.identitiesWithoutOwners > 0 ? `${d.identitiesWithoutOwners} identities without assigned owners` : "",
        inventoryBreakdown: d.details?.inventoryBreakdown || [],
        topDormant: d.details?.topDormant || [],
        managedBreakdown: d.details?.managedBreakdown || {},
      }} />;
    }
    case "secretsCredentials": {
      const rot = dashboardData.rotation;
      const cred = dashboardData.credentials;
      const sec = dashboardData.secrets;
      if (!rot && !cred && !sec) return null;
      return <ExpandedSecretsCredentialsCard
        cloudProvider={selectedCloud.toLowerCase()}
        rotation={{
          rotatedPct: rot?.rotatedPct || 0,
          pending: rot?.pending || 0,
          metrics: (rot?.details?.metrics as any) || { rotated: 0, pending: 0, failed: 0, scheduled: 0, avgDays: 0 },
          ageDistribution: rot?.details?.ageDistribution || [],
          recentRotations: rot?.details?.recentRotations || [],
        }}
        credentials={{
          totalKeys: cred?.totalKeys || 0,
          agedKeys: cred?.agedKeys || 0,
          rotationRatePct: cred?.rotationRatePct || 0,
          categories: cred?.categories || [],
          ageDistribution: cred?.details?.ageDistribution || [],
          topAgedKeys: cred?.details?.topAgedKeys || [],
          multipleKeyServiceAccounts: cred?.details?.multipleKeyServiceAccounts || [],
        }}
        secrets={{
          totalSecretsManaged: sec?.totalSecretsManaged || 0,
          compliancePct: (sec?.details as any)?.compliancePct || 0,
          issuesFound: sec?.issuesFound || [],
          compliantSecrets: sec?.details?.compliantSecrets || 0,
          nonCompliantSecrets: sec?.details?.nonCompliantSecrets || 0,
          secretsByType: sec?.details?.secretsByType || [],
          storageDistribution: (sec?.details as any)?.storageDistribution || sec?.details?.vaultDistribution || [],
          recentActivity: sec?.details?.recentActivity || [],
          topExposedSecrets: sec?.details?.topExposedSecrets || [],
        }}
      />;
    }
    case "correlations": {
      const d = dashboardData.correlations;
      if (!d) return null;
      // Provide backwards compatibility maps for cards when details are missing
      const fallbackServiceMap = (d.correlations || [])
        .filter(c => c.correlation?.service && (c.correlation?.secrets?.length || 0) > 0)
        .map(c => ({
          service: c.correlation.service,
          secrets: c.correlation.secrets || [],
          nhis: c.correlation.nhis?.length || 0,
          risk: (c.correlation.risk || 'low').toLowerCase(),
        }));
      const fallbackTopNhisList = (d.correlations || [])
        .filter(c => c.correlation?.service)
        .slice(0, 3)
        .map(c => ({
          name: c.correlation.service,
          secretsUsed: (c.correlation.secrets || []).length,
          services: 1,
          risk: c.severity === "High" || c.severity === "Critical" ? 90 : c.severity === "Medium" ? 70 : 40,
        }));
      return <ExpandedCorrelationsCard data={{
        cloudProvider: selectedCloud.toLowerCase(),
        serviceSecretMap: d.details?.serviceSecretMap || fallbackServiceMap,
        topNhis: d.details?.topNhis || fallbackTopNhisList,
        topSecrets: d.details?.topSecrets || [],
        insights: d.details?.insights || [],
        allRelations: d.details?.allRelations || d.correlations || [],
        cloudSaasCorrelations: d.details?.cloudToSaasCorrelations || d.details?.externalRelations || [],
        stats: (d.details?.stats as any) || {
          totalRelations: d.totalRelations,
          avgSecretsPerNhi: d.avgSecretsPerNhi ?? 0,
          avgNhisPerService: d.avgNhisPerService ?? 0,
        },
      }} />;
    }
    case "total-nhis": {
      const d = dashboardData.totalNhis;
      if (!d) return null;
      return <ExpandedTotalNhisCard data={{
        cloudProvider: selectedCloud.toLowerCase(),
        activePct: d.details?.activePct || 0,
        totalAlerts: d.details?.summary?.totalAlerts || 0,
        dormantServiceAccounts: d.details?.summary?.dormantServiceAccounts || 0,
        alertTrend: d.details?.alertTrend || [],
        rankedNhis: d.details?.rankedNhis || [],
        recentAlerts: d.details?.recentAlerts || []
      }} />;
    }
    case "total-secrets": {
      const d = dashboardData.activeSecrets;
      if (!d) return null;
      return <ExpandedTotalSecretsCard data={{
        cloudProvider: selectedCloud.toLowerCase(),
        activePct: d.details?.activePct || 0,
        alertTrend: d.details?.alertTrend || [],
        summary: d.details?.summary || {},
        rankedSecrets: d.details?.rankedSecrets || [],
        recentAlerts: d.details?.recentAlerts || []
      }} />;
    }
    case "review-aged-nhis": {
      const d = dashboardData.criticalRisks;
      if (!d) return null;
      return <ExpandedReviewAgedNhisCard data={{
        cloudProvider: selectedCloud.toLowerCase(),
        summary: d.details?.summary || {},
        reviewCandidates: d.details?.reviewCandidates || [],
        severityBreakdown: d.details?.severityBreakdown || { critical: 0, high: 0, medium: 0, low: 0 },
        recentAlerts: d.details?.recentAlerts || []
      }} />;
    }
    default:
      return null;
  }
};

const getCardTitle = (id: string): string => {
  switch (id) {
    case "riskScore": return "Overall Risk Score";
    case "lifecycle": return "NHI Lifecycle Registry";
    case "secretsCredentials": return "Secrets & Credentials";
    case "correlations": return "NHI Correlations";
    case "total-nhis": return "Total NHIs";
    case "total-secrets": return "Total Secrets";
    case "review-aged-nhis": return "Review Aged NHIs";
    default: return "Card Details";
  }
};

const Index = () => {
  const [cardOrder, setCardOrder] = useState(cardConfigs);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  // Cloud selector state
  const [tenantList, setTenantList] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedCloud, setSelectedCloud] = useState<CloudType>("GCP");
  const [selectedResource, setSelectedResource] = useState<CloudResource | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Self-heal HMR / missed configured cards
  useEffect(() => {
    setCardOrder((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      const newItems = cardConfigs.filter((c) => !existingIds.has(c.id));
      if (newItems.length > 0) {
        return [...newItems, ...prev];
      }
      return prev;
    });
  }, []);

  // Load backend configs for the active tab;
  const [cloudResources, setCloudResources] = useState<Record<CloudType, CloudResource[]>>({
    AWS: [],
    GCP: [],
    AZURE: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState<DashboardData>(emptyDashboard);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [cloudSaasCorrelations, setCloudSaasCorrelations] = useState<CloudSaasCorrelation[]>([]);
  const [saasApps, setSaasApps] = useState<SaasAppInfo[]>([]);

  const handleCardClick = (cardId: string) => {
    setExpandedCard(cardId);
    if (cardId === "correlations" && selectedTenant && selectedResource) {
      fetchCloudSaasCorrelations(selectedTenant.tenantName, selectedResource.id)
        .then(setCloudSaasCorrelations)
        .catch(() => setCloudSaasCorrelations([]));
    }
  };

  // Fetch tenants on mount
  useEffect(() => {
    const loadTenants = async () => {
      try {
        setIsLoading(true);
        const tenants = await fetchTenants();
        setTenantList(tenants);
        if (tenants.length > 0) {
          setSelectedTenant(tenants[0]);
        }
      } catch (error) {
        console.error("Failed to fetch tenants:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadTenants();
  }, []);

  // Fetch SaaS apps when tenant changes
  useEffect(() => {
    if (selectedTenant) {
      fetchConfiguredSaasApps(selectedTenant.tenantName)
        .then((data: any) => {
          const parseApps = (list: any[]): SaasAppInfo[] =>
            list
              .map((item): SaasAppInfo | null => {
                if (typeof item === 'string') {
                  const name = item.trim();
                  if (!name) return null;
                  return { name, integrations: 1, secrets: 0, status: 'connected' };
                }

                if (!item || typeof item !== 'object') return null;

                const name = [item.name, item.saasType, item.appName, item.provider, item.type]
                  .find((value) => typeof value === 'string' && value.trim().length > 0);

                if (!name) return null;

                return {
                  name,
                  integrations: Number(item.meta?.integrations ?? item.integrations ?? 1),
                  secrets: Number(item.meta?.secrets ?? item.secrets ?? 0),
                  status: item.status || 'connected',
                };
              })
              .filter((app): app is SaasAppInfo => Boolean(app && app.name.toLowerCase() !== 'unknown'));

          if (Array.isArray(data)) {
            setSaasApps(parseApps(data));
          } else if (data?.configuredSaasApps && Array.isArray(data.configuredSaasApps)) {
            setSaasApps(parseApps(data.configuredSaasApps));
          } else if (typeof data === 'object' && data !== null) {
            const appEntries = Object.entries(data).filter(([key, value]) =>
              !['tenantName', 'id', '_id', 'createdAt', 'updatedAt'].includes(key)
              && key.toLowerCase() !== 'unknown'
              && (Array.isArray(value) || typeof value === 'object' || value === true)
            );

            setSaasApps(
              appEntries.map(([key, value]) => ({
                name: key,
                integrations: typeof value === 'object' && value && !Array.isArray(value)
                  ? Number((value as any).meta?.integrations ?? (value as any).integrations ?? 1)
                  : 1,
                secrets: typeof value === 'object' && value && !Array.isArray(value)
                  ? Number((value as any).meta?.secrets ?? (value as any).secrets ?? 0)
                  : 0,
                status: typeof value === 'object' && value && !Array.isArray(value)
                  ? ((value as any).status || 'connected')
                  : 'connected',
              }))
            );
          } else {
            setSaasApps([]);
          }
        })
        .catch(() => setSaasApps([]));
    }
  }, [selectedTenant]);

  // Fetch cloud resources when tenant changes
  const loadCloudResources = useCallback(async (tenant: Tenant) => {
    setIsLoading(true);
    try {
      const [awsAccounts, gcpProjects, azureSubscriptions] = await Promise.all([
        fetchAwsAccounts(tenant.tenantName).catch(() => []),
        fetchGcpProjectIds(tenant.tenantName).catch(() => []),
        fetchAzureSubscriptions(tenant.tenantName).catch(() => []),
      ]);

      const newResources: Record<CloudType, CloudResource[]> = {
        AWS: awsAccounts.map((acc) => ({ id: acc.accountId, name: acc.accountName || acc.accountId })),
        GCP: gcpProjects.map((proj) => ({ id: proj, name: proj })),
        AZURE: azureSubscriptions.map((sub) => ({ id: sub.azureSubscriptionId, name: sub.subscriptionName || sub.azureSubscriptionId })),
      };

      setCloudResources(newResources);

      // Auto-select GCP first by default, then fallback to others
      if (newResources.GCP.length > 0) {
        setSelectedCloud("GCP");
        setSelectedResource(newResources.GCP[0]);
      } else if (newResources.AWS.length > 0) {
        setSelectedCloud("AWS");
        setSelectedResource(newResources.AWS[0]);
      } else if (newResources.AZURE.length > 0) {
        setSelectedCloud("AZURE");
        setSelectedResource(newResources.AZURE[0]);
      } else {
        setSelectedResource(null);
      }
    } catch (error) {
      console.error("Failed to fetch cloud resources:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      loadCloudResources(selectedTenant);
    }
  }, [selectedTenant, loadCloudResources]);

  // Fetch dashboard data when tenant, cloud, or resource changes
  const loadDashboardData = useCallback(async (tenantName: string, cloudAccountId: string, cloudType: CloudType) => {
    setIsDashboardLoading(true);
    setDashboardData(emptyDashboard);

    try {
      const [
        totalNhis,
        activeSecrets,
        criticalRisks,
        riskScore,
        lifecycle,
        rotation,
        credentials,
        secrets,
        review,
        correlations,
      ] = await Promise.all([
        fetchTotalNhis(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchActiveSecrets(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchCriticalRisks(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchOverallRiskScore(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchNhiLifecycleRegistry(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchRotation(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchStaticCredentials(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchSecretsPosture(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchNhiReview(tenantName, cloudAccountId, cloudType).catch(() => null),
        fetchNhiCorrelations(tenantName, cloudAccountId, cloudType).catch(() => null),
      ]);

      setDashboardData({
        totalNhis,
        activeSecrets,
        criticalRisks,
        riskScore,
        lifecycle,
        rotation,
        credentials,
        secrets,
        review,
        correlations,
      });
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setIsDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTenant && selectedResource) {
      loadDashboardData(selectedTenant.tenantName, selectedResource.id, selectedCloud);
    }
  }, [selectedTenant, selectedResource, selectedCloud, loadDashboardData]);

  useEffect(() => {
    if (!selectedTenant || !selectedResource) {
      return;
    }

    myLocalStorage.setItem("latestTenant", selectedTenant);
    myLocalStorage.setItem("gcpSelectedProjectId", "");
    myLocalStorage.setItem("selectedAwsAccount", "");
    setStructuredLocalStorageValue("awsSelectedAccountId", "");
    setStructuredLocalStorageValue("azureSelectedSubscriptionId", "");

    if (selectedCloud === "GCP") {
      myLocalStorage.setItem("gcpSelectedProjectId", selectedResource.id);
    } else if (selectedCloud === "AWS") {
      myLocalStorage.setItem("selectedAwsAccount", selectedResource.id);
      setStructuredLocalStorageValue("awsSelectedAccountId", selectedResource.id);
    } else {
      myLocalStorage.setItem(
        "latestSubList",
        JSON.stringify({
          azureSubscriptionId: selectedResource.id,
          subscriptionName: selectedResource.name,
          name: selectedResource.name,
        }),
      );
      setStructuredLocalStorageValue(
        "azureSelectedSubscriptionId",
        selectedResource.id,
      );
    }

    window.dispatchEvent(new CustomEvent("cloud-selection-changed"));
  }, [selectedCloud, selectedResource, selectedTenant]);

  const handleTenantChange = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setSelectedResource(null);
  };

  const handleCloudChange = (cloud: CloudType) => {
    setSelectedCloud(cloud);
    const resources = cloudResources[cloud];
    setSelectedResource(resources.length > 0 ? resources[0] : null);
  };

  const handleResourceChange = (resource: CloudResource) => {
    setSelectedResource(resource);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const cardNodeMapRef = useRef<Map<string, HTMLDivElement>>(new Map());

  const registerNode = useCallback((id: string, node: HTMLDivElement | null) => {
    if (node) {
      cardNodeMapRef.current.set(id, node);
    } else {
      cardNodeMapRef.current.delete(id);
    }
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveId(id);

    const node = cardNodeMapRef.current.get(id);
    if (node) {
      const rect = node.getBoundingClientRect();
      setOverlaySize({ width: rect.width, height: rect.height });
      return;
    }

    const initial = event.active.rect.current.initial;
    if (initial) {
      setOverlaySize({ width: initial.width, height: initial.height });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverlaySize(null);

    if (over && active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverlaySize(null);
  };

  const handleRefreshSnapshots = async () => {
    if (!selectedTenant || !selectedResource) return;
    try {
      setIsRefreshing(true);
      await refreshDashboardSnapshots(selectedTenant.tenantName, selectedResource.id, selectedCloud);
      // Reload dashboard after successful snapshot generation
      loadDashboardData(selectedTenant.tenantName, selectedResource.id, selectedCloud);
    } catch (error) {
      console.error("Failed to update snapshots:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const [overlaySize, setOverlaySize] = useState<{ width: number; height: number } | null>(null);
  const activeCard = cardOrder.find((c) => c.id === activeId);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-xl relative z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="w-full">
            <CloudSelector
              tenantList={tenantList}
              selectedTenant={selectedTenant}
              selectedCloud={selectedCloud}
              selectedResource={selectedResource}
              cloudResources={cloudResources}
              onTenantChange={handleTenantChange}
              onCloudChange={handleCloudChange}
              onResourceChange={handleResourceChange}
              onRefreshSnapshots={handleRefreshSnapshots}
              isRefreshing={isRefreshing}
              isLoading={isLoading}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        {/* <QuickStats data={dashboardData} onCardClick={handleCardClick} /> */}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 grid-flow-dense gap-x-4 auto-rows-[1px] gap-y-0 mb-6">
          <SortableContext items={cardOrder.map((c) => c.id)} strategy={rectSortingStrategy}>
            {cardOrder.map((card) => (
                <SortableCard 
                  key={card.id} 
                  id={card.id} 
                  size={card.size} 
                  content={renderCard(card.id, dashboardData, card.expandable ? handleCardClick : undefined, selectedCloud, saasApps)}
                  activeId={activeId}
                  expandedCard={expandedCard}
                  registerNode={registerNode}
                />
              ))}
            </SortableContext>
          </div>

          <DragOverlay adjustScale={false} dropAnimation={null}>
            {activeCard && overlaySize ? (
              <div
                style={{ width: overlaySize.width, height: overlaySize.height }}
                className="shadow-2xl shadow-primary/20 rounded-2xl overflow-hidden ring-2 ring-sky-500/30"
              >
                <div className="h-full bg-card">
                  {renderCard(activeCard.id, dashboardData, undefined, selectedCloud, saasApps)}
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

      </main>

      {/* Expanded Card Dialog */}
      <ExpandedCardDialog
        open={expandedCard !== null}
        onOpenChange={(open) => !open && setExpandedCard(null)}
        title={expandedCard ? getCardTitle(expandedCard) : undefined}
      >
        {expandedCard && renderExpandedCard(expandedCard, dashboardData, selectedCloud, cloudSaasCorrelations)}
      </ExpandedCardDialog>
    </div>
  );
};

export default Index;
